// Created on 2023-08-03
// Created by Vitaut Tryputsin
//
// Copyright (c) 2023 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
import { OccViewer } from "../viewer/viewer.js";
/**
 * Auxiliary tool for math operations between 3D vectors.
 */
export class Vec3Tools {
    /** Return DX vector. */
    static dx() { return { x: 1.0, y: 0.0, z: 0.0 }; }
    /** Return DY vector. */
    static dy() { return { x: 0.0, y: 1.0, z: 0.0 }; }
    /** Return DZ vector. */
    static dz() { return { x: 0.0, y: 0.0, z: 1.0 }; }
    /** Return invalid vector (`RealLast()` values). */
    static invalid() {
        return { x: 1.7976931348623158e+308, y: 1.7976931348623158e+308, z: 1.7976931348623158e+308 };
    }
    /** Return length of vector. */
    static len(theV) {
        return Math.sqrt(theV.x * theV.x + theV.y * theV.y + theV.z * theV.z);
    }
    /** Return TRUE if point has infinite range (same as `Precision::IsInfinite()` check in OCCT). */
    static isInfinite(theA) {
        if (typeof (theA) === 'number') {
            return Math.abs(theA) >= (0.5 * 2.e+100);
        }
        return Math.abs(theA.x) >= (0.5 * 2.e+100) || Math.abs(theA.y) >= (0.5 * 2.e+100)
            || Math.abs(theA.z) >= (0.5 * 2.e+100);
    }
    /** Return TRUE if vector is equal to zero vector. */
    static isZero(theA) {
        return this.isEqual(theA, { x: 0, y: 0, z: 0 });
    }
    /** Return TRUE if vector is equal to zero vector with tolerance. */
    static isZeroWithTolerance(theA, theTol) {
        return this.isEqualWithTolerance(theA, { x: 0, y: 0, z: 0 }, theTol);
    }
    /** Return TRUE if vectors are equal. */
    static isEqual(theA, theB) {
        return theA.x == theB.x && theA.y == theB.y && theA.z == theB.z;
    }
    /** Return TRUE if vectors are equal with tolerance. */
    static isEqualWithTolerance(theA, theB, theTol) {
        return Math.abs(theA.x - theB.x) <= theTol && Math.abs(theA.y - theB.y) <= theTol
            && Math.abs(theA.z - theB.z) <= theTol;
    }
    /** Return opposite vector. */
    static reverse(theV) {
        return { x: -theV.x, y: -theV.y, z: -theV.z };
    }
    /** Return coordinate with specified index within [0,2] range. */
    static coord(theV, theI) {
        switch (theI) {
            case 0: return theV.x;
            case 1: return theV.y;
            case 2: return theV.z;
        }
        throw new Error("Out of range index");
    }
    /** Compute per-component addition between two vectors. */
    static add(theA, theB) {
        if (typeof (theB) === 'number') {
            return { x: theA.x + theB, y: theA.y + theB, z: theA.z + theB };
        }
        return this._myViewer.vec3ToolsAdd(theA, theB);
    }
    /** Compute per-component subtraction between two vectors. */
    static subtract(theA, theB) {
        if (typeof (theB) === 'number') {
            return { x: theA.x - theB, y: theA.y - theB, z: theA.z - theB };
        }
        return this._myViewer.vec3ToolsSubtract(theA, theB);
    }
    /** Compute per-component multiplication between two vectors. */
    static multiply(theA, theB) {
        if (typeof (theB) === 'number') {
            return { x: theA.x * theB, y: theA.y * theB, z: theA.z * theB };
        }
        return this._myViewer.vec3ToolsMultiply(theA, theB);
    }
    /** Normalize vector. */
    static normalize(theV) {
        return this._myViewer.vec3ToolsNormalize(theV);
    }
    /** Compute dot product between two vectors. */
    static dot(theA, theB) {
        return theA.x * theB.x + theA.y * theB.y + theA.z * theB.z;
    }
    /** Compute per-component division between two vectors. */
    static divide(theA, theB) {
        if (typeof (theB) === 'number') {
            return { x: theA.x / theB, y: theA.y / theB, z: theA.z / theB };
        }
        return this._myViewer.vec3ToolsDivide(theA, theB);
    }
    /** Compute cross product between two vectors. */
    static cross(theA, theB) {
        return this._myViewer.vec3ToolsCross(theA, theB);
    }
    /** Apply matrix to vector */
    static applyMatrix(theMatrix, thePoint) {
        return this._myViewer.vec3ToolsApplyMatrix(theMatrix, thePoint);
    }
    /** get Angle*/
    static angle(theV1, theV2) {
        const a = Vec3Tools.len(theV1);
        const b = Vec3Tools.len(theV2);
        const c = Vec3Tools.dot(theV1, theV2);
        return Math.acos(c / (a * b));
    }
    /** set Viewer */
    static setViewer(theViewer) {
        Vec3Tools._myViewer = theViewer;
    }
}
/** Return _myViewer. */
Vec3Tools._myViewer = null;
/**
 * Update [[ModelLoader]] in OccViewer.
 * @internal
 */
OccViewer.onInit((theModule) => {
    Vec3Tools.setViewer(theModule);
});
